import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { SelectionModel } from '@angular/cdk/collections';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatLegacyCheckbox as MatCheckbox } from '@angular/material/legacy-checkbox';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';

import { ActionConfig } from '../models/action-config.model';

@Component({
  selector: 'app-data-table',
  standalone: false,
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')
      ),
    ]),
  ],
})
export class DataTableComponent implements OnInit, OnChanges {
  @Input() tableData: any;
  @Input() tableDataLength: number;
  @Input() columnsHeader: Object;
  @Input() isFilterable = false;
  @Input() isSelectable = false;
  @Input() hasDetailRenderer = false;
  @Input() configAction: ActionConfig;
  @Input() paginationSizes: number[] = [5, 10, 15];
  @Input() defaultPageSize = this.paginationSizes[1];
  @Input() searchText: string = '';
  @Input() cssClass: string;
  @Input() rotateActionIcon: boolean = false;
  @Input() tooltipActionIcon: string = '';
  @Input() tableOperationCase: boolean = false;
  @Input() accountOperationCase: boolean = false;
  @Input() characterLimit: number = 21;
  // @Input() disabledButtons:

  /** Emite os itens selecionados **/
  @Output() selected: EventEmitter<any> = new EventEmitter<any>();
  /** Emite o evento quando clica na linha **/
  @Output() rowAction: EventEmitter<any> = new EventEmitter<any>();
  /** Emite o evento quando clica na td da coluna ação **/
  @Output() action: EventEmitter<any> = new EventEmitter<any>();
  @Output() actionMenu: EventEmitter<any> = new EventEmitter<any>();
  @Output() actionEdit: EventEmitter<any> = new EventEmitter<any>();
  @Output() actionView: EventEmitter<any> = new EventEmitter<any>();
  @Output() actionDelete: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('allItemsCheckBox') allItemsCheckBox: MatCheckbox;

  selection = new SelectionModel<any>(true, []);
  objectKeys = Object.keys;
  objectValues = Object.values;
  dataSource: MatTableDataSource<any> = new MatTableDataSource();
  expandedElement: any;
  filterValue = '';
  privateCss: string = 'mat-table';

  @ViewChild('filterRef') filterRef: ElementRef;

  regex = /,/g;
  showDataTooltip: boolean;

  applyFilter(): void {
    if (this.isFilterable)
      this.dataSource.filter = this.filterValue?.trim().toLowerCase() || '';
  }

  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  isPageSelected(): boolean {
    const _pageData = this.pageData() || [];
    const hasAnyRowUnselected = _pageData.find((item) => {
      return !this.selection.isSelected(item);
    });
    return !hasAnyRowUnselected;
  }

  /** Seleciona todas as linhas, se não estiverem todasselecionadas; caso contrário, limpar a seleção. **/
  indeterminateChange(status: boolean): void {
    if (!status && this.selection.hasValue() && !this.isAllSelected())
      this.allItemsCheckBox.indeterminate = true;
  }
  /** Seleciona todas as linhas, se não estiverem todasselecionadas; caso contrário, limpar a seleção. **/
  masterToggle(): void {
    this.isAllSelected()
      ? this.selection.clear()
      : this.isPageSelected()
        ? this.selectAllItems()
        : this.selectPage();
    this.emitSelected();
  }

  // formata os dados da tabela adicionando ... e um tooltip caso o valor seja muito grande.
  formatLengthTableData(value: string): string {
    if (value?.length > this.characterLimit) {
      return value.substring(0, this.characterLimit) + '...';
    }
    return value;
  }

  selectItem(row: any): void {
    this.selection.toggle(row);
    this.emitSelected();
  }

  selectPage(): void {
    this.pageData().forEach((row) => this.selection.select(row));
  }

  selectAllItems(): void {
    this.dataSource.data.forEach((row) => this.selection.select(row));
  }

  /** aria-label do checkbox **/
  checkboxLabel(row?: any): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.position + 1
      }`;
  }

  rowClick(row: any): void {
    this.toggleColappse(row);
    //this.rowAction.emit(row)

    if (row === this.expandedElement) this.rowAction.emit(row);
    else this.rowAction.emit(null);
  }

  emitAction(row: any, event: Event): void {
    event.stopPropagation();
    this.action.emit(row);
  }

  emitActionOpenMenu(
    row: any,
    action: string,
    disabled: boolean,
    event: Event
  ): void {
    let actionEmitted = {
      row: row,
      action: action,
      disabled: disabled,
    };
    this.actionMenu.emit(actionEmitted);
  }

  emitActionEdit(row: any, event: Event): void {
    event.stopPropagation();
    this.actionEdit.emit(row);
  }

  emitActionView(row: any, event: Event): void {
    event.stopPropagation();
    this.actionView.emit(row);
  }

  emitActionDelete(row: any, event: Event): void {
    event.stopPropagation();
    this.actionDelete.emit(row);
  }

  clearSelection(): void {
    this.selection.clear();
    this.allItemsCheckBox.checked = false;
  }

  clearFilter(): void {
    this.filterValue = '';
    this.dataSource.filter = '';
  }

  emitSelected(): void {
    this.selected.emit(this.selection.selected);
  }

  private toggleColappse(target): void {
    this.expandedElement = this.expandedElement === target ? null : target;
  }

  private addSelectIncolumnsHeader(): void {
    this.columnsHeader = Object.assign(
      { select: 'select' },
      this.columnsHeader
    );
  }

  private addActionIncolumnsHeader(): void {
    this.columnsHeader = Object.assign(this.columnsHeader, {
      action: this.configAction.columnDisplayName,
    });
  }

  ngOnInit() {
    if (!!this.configAction) {
      this.addActionIncolumnsHeader();
    }
  }

  ngAfterViewInit() { }

  refreshData() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.dataSource.data = this.tableData;

    if (!!this.isSelectable) {
      this.addSelectIncolumnsHeader();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.refreshData();
    this.applyFilter();

    if (this.cssClass) this.privateCss = 'mat-table ' + this.cssClass;
    else this.privateCss = 'mat-table';
  }

  getFilteredData() {
    return this.dataSource.filteredData;
  }

  getRowClass(row) {
    var values = Object.keys(row).map(function (e) {
      return row[e];
    });
    return values.toString().replace(this.regex, ' ');
  }

  deselect(itens: Array<any>) {
    const itensToRemoveSelection = this.dataSource.data.filter((d) =>
      itens.includes(d.key)
    );
    this.dataSource.data.forEach((row) => this.selection.deselect(row));
    this.emitSelected();
  }

  /**
   * Returns a paged slice of the provided data array according to the provided MatPaginator's page
   * index and length. If there is no paginator provided, returns the data array as provided.
   * @return {?}
   */
  pageData() {
    if (!this.paginator) {
      return [];
    }

    const startIndex = this.paginator.pageIndex * this.paginator.pageSize;
    return this.dataSource.data.slice(
      startIndex,
      startIndex + this.paginator.pageSize
    );
  }

  getContentAccountOperationCase(element: any): string {
    if (this.accountOperationCase) {
      if (element?.quantidadeContas === 0) {
        return 'este operador não possui contas vinculadas';
      }
      if (this.tableDataLength === 1) {
        return 'esta empresa possui apenas um operador';
      }
      return 'importar em lote';
    }
  }
}
